import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  prescriptionQueryKeys,
  prescriptionsApi,
} from "../../shared/api/prescriptions";
import type {
  PrescriptionListFilter,
  PrescriptionStatus,
} from "../../shared/types/prescription";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import { describeRange } from "../../shared/dateRange";
import { formatDate } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { FilterBar } from "../../shared/ui/FilterBar";
import { DateRangePicker } from "../../shared/ui/DateRangePicker";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<PrescriptionStatus, string> = {
  DRAFT: "Nháp",
  SIGNED: "Đã hoàn tất",
  CANCELLED: "Đã hủy",
};
const STATUS_VARIANT: Record<
  PrescriptionStatus,
  NonNullable<BadgeProps["variant"]>
> = { DRAFT: "warning", SIGNED: "success", CANCELLED: "destructive" };

export function PrescriptionListPage() {
  // Bộ lọc nằm trên URL (kiểu SalePro admin): reload hay quay về từ một đơn thuốc vẫn giữ nguyên
  // danh sách. Vắng mặt = không lọc.
  const { filters, set, reset } = useUrlFilters({
    q: param.string(),
    status: param.oneOf(Object.keys(STATUS_LABEL) as PrescriptionStatus[]),
    doctorId: param.string(),
    from: param.isoDate(),
    to: param.isoDate(),
    page: param.int(),
  });
  const { q, status, doctorId, from: fromDate, to: toDate, page } = filters;
  const debouncedQ = useDebounce(q.trim(), 300);

  const filter: PrescriptionListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      status: status || null,
      doctorId: doctorId || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
    }),
    [debouncedQ, status, doctorId, fromDate, toDate]
  );
  const filtered = Boolean(debouncedQ || status || doctorId || fromDate || toDate);

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
  });

  const query = useQuery({
    queryKey: prescriptionQueryKeys.list(filter, page),
    queryFn: () => prescriptionsApi.list(filter, { page, size: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 0;

  return (
    <div>
      <PageHeader
        title="Đơn thuốc"
        description="Tất cả đơn thuốc của phòng khám."
        /*
         * Lối vào "Toa thuốc mẫu" (#19412). Màn đó đã xây xong từ `d46e1316` và có dữ liệu thật,
         * nhưng CHƯA BAO GIỜ được nối vào đây hay vào menu trái — chỉ vào được bằng cách gõ URL
         * (hoặc qua hai liên kết trong màn khám và một ô ở Trang chủ, những chỗ không ai đi tìm
         * một trang quản lý). Đặt ở đây vì đây là chỗ người ta mở ra để tìm nó.
         *
         * Không thêm mục vào menu trái: `nav-config.ts` là danh sách PHẲNG, không có mục con, nên
         * một mục mới sẽ thành ngang hàng với "Đơn thuốc" chứ không phải nằm dưới nó — sai quan hệ
         * thật giữa hai màn.
         */
        actions={
          <Button asChild variant="outline">
            <Link to="/prescriptions/templates">Toa thuốc mẫu</Link>
          </Button>
        }
      />

      <FilterBar
        search={{
          value: q,
          onChange: (v) => set({ q: v }),
          placeholder: "Tìm theo tên bệnh nhân, số ĐT, mã BN…",
          autoFocus: true,
          wide: true,
        }}
        onClearAll={() => reset()}
        fields={[
          {
            key: "status",
            label: "Trạng thái",
            value: status ? STATUS_LABEL[status] : null,
            onClear: () => set({ status: "" }),
            render: (id) => (
              <Select
                value={status || "all"}
                onValueChange={(v) =>
                  set({ status: v === "all" ? "" : (v as PrescriptionStatus) })
                }
              >
                <SelectTrigger id={id} aria-label="Lọc trạng thái">
                  <SelectValue placeholder="Tất cả trạng thái" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tất cả trạng thái</SelectItem>
                  {(Object.keys(STATUS_LABEL) as PrescriptionStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ),
          },
          {
            key: "doctor",
            label: "Bác sĩ kê đơn",
            value: doctorId
              ? (doctors.data?.find((d) => d.id === doctorId)?.name ?? "Đã chọn")
              : null,
            onClear: () => set({ doctorId: "" }),
            render: (id) => (
              <Select
                value={doctorId || "all"}
                onValueChange={(v) => set({ doctorId: v === "all" ? "" : v })}
              >
                <SelectTrigger id={id} aria-label="Lọc bác sĩ">
                  <SelectValue placeholder="Mọi bác sĩ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Mọi bác sĩ</SelectItem>
                  {doctors.data?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ),
          },
          {
            key: "range",
            label: "Ngày kê",
            value: describeRange(fromDate, toDate),
            onClear: () => set({ from: "", to: "" }),
            render: (id) => (
              <DateRangePicker
                id={id}
                from={fromDate}
                to={toDate}
                onChange={({ from, to }) => set({ from, to })}
              />
            ),
          },
        ]}
      />

      {query.isError && (
        <p className="mb-4 text-sm text-destructive">
          Không tải được danh sách đơn thuốc.
        </p>
      )}

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ngày</TableHead>
              <TableHead>Bệnh nhân</TableHead>
              <TableHead>Bác sĩ</TableHead>
              <TableHead className="text-center">Số thuốc</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  {filtered ? "Không có đơn thuốc nào khớp bộ lọc." : "Chưa có đơn thuốc nào."}
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="whitespace-nowrap">
                  {formatDate(p.createdAt)}
                </TableCell>
                <TableCell>
                  <PatientLink id={p.patientId} name={p.patientName} />
                  {p.patientCode && (
                    <span className="font-mono text-muted-foreground">
                      {" "}
                      · {p.patientCode}
                    </span>
                  )}
                </TableCell>
                <TableCell>{p.doctorName}</TableCell>
                <TableCell className="text-center tabular-nums">
                  {p.itemCount}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANT[p.status]}>
                    {STATUS_LABEL[p.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/prescriptions/${p.id}`}>Xem</Link>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => set({ page: Math.max(0, page - 1) })}
          >
            <ChevronLeft />
            Trước
          </Button>
          <span className="text-sm text-muted-foreground">
            Trang {page + 1} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => set({ page: page + 1 })}
          >
            Sau
            <ChevronRight />
          </Button>
        </div>
      )}
    </div>
  );
}
